import React from 'react';
import useMedicalProfileFiles from '../../../hooks/api/useMedicalProfileFiles';
import { getIntl } from '../../../utils/intl';
import { APITypes } from '../../../primary-record-types';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Link from '../../v2BaseComponents/Link';
import Text from '../../v2BaseComponents/Text';
import Stack from '../../v2BaseComponents/Stack';
import Skeleton from '../../v2BaseComponents/Skeleton';

const intl = getIntl('FileFolderPath', {
    home: 'Home',
});

export type FileFolderPathOwnProps = {
    onFolderSelected: (folderId: string) => void;
    folderId: string;
    medicalProfileId: string;
    fileName?: string;
    skeltonFallback?: boolean;
    shortenPath?: boolean;
    mobileMaxPathLength?: number;
};

export type FileFolderPathProps = FileFolderPathOwnProps;

const MAX_FOLDER_PATH_LENGTH = {
    MOBILE: 30,
    DESKTOP: 100,
};

const FileFolderPath: React.FC<FileFolderPathProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const { isLoadingFolderMetadata, folderMetadata } = useMedicalProfileFiles({
        medicalProfileId: props.medicalProfileId,
        folderId: props.folderId,
    });

    if (isLoadingFolderMetadata || !folderMetadata) {
        return props.skeltonFallback ? (
            <Skeleton
                style={{
                    width: '100%',
                    height: '100%',
                }}
            />
        ) : null;
    }

    const getParts = () => {
        const getAllParts = () => {
            const rootPart: APITypes.medicalProfileFilesService.FolderPathPart = {
                folderName: intl('home'),
                folderId: 'root',
            };

            if (folderMetadata.targetFolder.isRoot) {
                return [rootPart];
            } else {
                return [rootPart, ...folderMetadata.targetFolder.folder.fullFolderPath];
            }
        };

        const getMaxPathLength = () => {
            const mobileMaxPathLength = props.mobileMaxPathLength || MAX_FOLDER_PATH_LENGTH.MOBILE;
            return isMobile || props.shortenPath ? mobileMaxPathLength : MAX_FOLDER_PATH_LENGTH.DESKTOP;
        };

        const maybePartialParts = [];
        const allParts = getAllParts();
        do {
            maybePartialParts.unshift(allParts.pop()!);
        } while (allParts.length > 0 && maybePartialParts.join('/').length < getMaxPathLength());
        if (allParts.length === 0) {
            return maybePartialParts;
        } else {
            return [
                {
                    folderName: '.. ',
                    folderId: allParts[allParts.length - 1].folderId,
                },
                ...maybePartialParts,
            ];
        }
    };

    const parts = getParts();

    const elements = parts.map((part, index) => {
        return (
            <React.Fragment key={part.folderId}>
                <Link
                    variant="body-1"
                    onPress={() => props.onFolderSelected(part.folderId)}
                    testID={`folder-breadcrumb-${part.folderName}`}
                >
                    {part.folderName}
                </Link>
                {index < parts.length - 1 || props.fileName ? <Text variant={'body-1'}>{' /'}</Text> : null}
            </React.Fragment>
        );
    });

    if (props.fileName) {
        elements.push(
            <React.Fragment key={props.fileName}>
                <Text variant={'body-1'}>{' ' + props.fileName}</Text>
            </React.Fragment>,
        );
    }

    return (
        <Stack horizontal align={'center'}>
            {elements}
        </Stack>
    );
};

export default React.memo(FileFolderPath);
